import { useEffect, useState } from 'react';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from './Button.tsx';
import { Card } from './Card.tsx';
import { useToast } from './Toaster.tsx';

export interface AvailabilityWindow {
  dayOfWeek: number;   // 0 = Sunday … 6 = Saturday
  startMinute: number; // 0..1439
  endMinute: number;   // 1..1440
}

interface Props {
  /** The agent whose availability we're showing/editing. */
  agentId: string;
  initial: AvailabilityWindow[];
  /** If true, render the editor (Save button). Otherwise read-only. */
  canEdit: boolean;
  /** Called after a successful save with the new windows. */
  onSaved?: (windows: AvailabilityWindow[]) => void;
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function toHHMM(minute: number): string {
  const h = Math.floor(minute / 60);
  const m = minute % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function fromHHMM(value: string): number | null {
  const match = value.match(/^(\d{2}):(\d{2})$/);
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return h * 60 + m;
}

function formatRange(w: AvailabilityWindow): string {
  return `${formatClock(w.startMinute)} – ${formatClock(w.endMinute)}`;
}

function formatClock(minute: number): string {
  const h24 = Math.floor(minute / 60) % 24;
  const m = minute % 60;
  const ampm = h24 < 12 ? 'AM' : 'PM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return m === 0 ? `${h12} ${ampm}` : `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

export function AvailabilityCard({ agentId, initial, canEdit, onSaved }: Props): React.ReactElement {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  // Editor state: one possibly-disabled slot per day-of-week, indexed
  // Mon..Sun (1..6,0) so the form reads naturally.
  const [draft, setDraft] = useState<Record<number, { enabled: boolean; start: string; end: string }>>(() =>
    blankDraft(initial),
  );

  // Re-hydrate the editor whenever the parent data changes.
  useEffect(() => {
    setDraft(blankDraft(initial));
  }, [initial]);

  function blankDraft(windows: AvailabilityWindow[]): Record<number, { enabled: boolean; start: string; end: string }> {
    const map: Record<number, { enabled: boolean; start: string; end: string }> = {};
    for (let d = 0; d < 7; d++) {
      map[d] = { enabled: false, start: '10:00', end: '13:00' };
    }
    for (const w of windows) {
      map[w.dayOfWeek] = {
        enabled: true,
        start: toHHMM(w.startMinute),
        end: toHHMM(w.endMinute),
      };
    }
    return map;
  }

  async function save(): Promise<void> {
    const windows: AvailabilityWindow[] = [];
    for (let d = 0; d < 7; d++) {
      const row = draft[d]!;
      if (!row.enabled) continue;
      const start = fromHHMM(row.start);
      const end = fromHHMM(row.end);
      if (start === null || end === null) {
        toast.error(`${DAY_LABELS[d]}: time is not valid.`);
        return;
      }
      if (end <= start) {
        toast.error(`${DAY_LABELS[d]}: end time must be after start time.`);
        return;
      }
      windows.push({ dayOfWeek: d, startMinute: start, endMinute: end });
    }
    setBusy(true);
    try {
      const r = await apiFetch<{ availability: AvailabilityWindow[] }>(
        `/api/agents/${agentId}/availability`,
        { method: 'PUT', json: { windows } },
      );
      toast.success('Availability updated.');
      setEditing(false);
      onSaved?.(r.availability);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  // Display order: Monday first (the typical work week), Sunday last.
  const displayOrder = [1, 2, 3, 4, 5, 6, 0];

  return (
    <Card>
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-[var(--color-header)]">Weekly availability</h2>
          <p className="mt-1 text-xs text-[var(--color-muted)]">
            Times are Eastern (America/New_York). Use this to publish working hours so other techs
            know when you're around.
          </p>
        </div>
        {canEdit && !editing && (
          <Button variant="outline" onClick={() => setEditing(true)}>
            {initial.length === 0 ? '+ Set availability' : 'Edit'}
          </Button>
        )}
      </div>

      {!editing && (
        <div className="mt-3">
          {initial.length === 0 ? (
            <p className="text-sm italic text-[var(--color-muted)]">
              No availability set yet.
            </p>
          ) : (
            <ul className="divide-y divide-[var(--color-border)] text-sm">
              {displayOrder.map((d) => {
                const w = initial.find((x) => x.dayOfWeek === d);
                return (
                  <li key={d} className="flex items-center justify-between py-2">
                    <span className="font-semibold text-[var(--color-header)]">
                      {fullDayLabel(d)}
                    </span>
                    <span className={w ? 'text-[var(--color-body)]' : 'text-[var(--color-muted)]'}>
                      {w ? formatRange(w) : 'Off'}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {editing && (
        <div className="mt-3 space-y-2">
          {displayOrder.map((d) => {
            const row = draft[d]!;
            return (
              <div
                key={d}
                className="flex flex-wrap items-center gap-3 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm"
              >
                <label className="flex w-24 items-center gap-2 font-semibold text-[var(--color-header)]">
                  <input
                    type="checkbox"
                    checked={row.enabled}
                    onChange={(e) =>
                      setDraft((cur) => ({ ...cur, [d]: { ...row, enabled: e.target.checked } }))
                    }
                    className="h-4 w-4 accent-[var(--color-t3-blue)]"
                  />
                  {fullDayLabel(d)}
                </label>
                <input
                  type="time"
                  value={row.start}
                  disabled={!row.enabled}
                  onChange={(e) =>
                    setDraft((cur) => ({ ...cur, [d]: { ...row, start: e.target.value } }))
                  }
                  className="h-8 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2 text-sm text-[var(--color-body)] disabled:bg-[var(--color-surface)] disabled:text-[var(--color-muted)]"
                />
                <span className="text-[var(--color-muted)]">to</span>
                <input
                  type="time"
                  value={row.end}
                  disabled={!row.enabled}
                  onChange={(e) =>
                    setDraft((cur) => ({ ...cur, [d]: { ...row, end: e.target.value } }))
                  }
                  className="h-8 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2 text-sm text-[var(--color-body)] disabled:bg-[var(--color-surface)] disabled:text-[var(--color-muted)]"
                />
                <span className="text-xs text-[var(--color-muted)]">EST</span>
              </div>
            );
          })}
          <div className="flex gap-2 pt-2">
            <Button onClick={() => void save()} disabled={busy}>
              {busy ? 'Saving…' : 'Save availability'}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => {
                setDraft(blankDraft(initial));
                setEditing(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function fullDayLabel(d: number): string {
  return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][d]!;
}
